import React, { useState, useEffect } from 'react';
import { authAPI, businessAPI } from '../services/api';
import { getApiErrorMessage, validateRegistrationPassword } from '../utils/authValidation';
import GoogleAuthButton from '../components/GoogleAuthButton';
import { useGoogleSignInEnabled } from '../hooks/useGoogleSignInEnabled';
import { getCurrencyOptions } from '../utils/currencyOptions';

interface Country {
  code: string;
  name: string;
  default_currency_code: string;
  default_date_format: string;
  default_number_format: string;
  default_time_format: string;
  default_first_day_of_week: number;
}

interface Currency {
  code: string;
  name: string;
  symbol: string;
}

const Register: React.FC<{ onLogin: () => void }> = ({ onLogin }) => {
  const [step, setStep] = useState(1); // 1: User info, 2: Business info, 3: Complete
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);

  const [userData, setUserData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: ''
  });

  const [businessData, setBusinessData] = useState({
    name: '',
    operational_name: '',
    email: '',
    phone: '',
    address: '',
    tax_identification_number: '',
    country_code: '',
    base_currency_code: '',
    date_format: '',
    number_format: '',
    time_format: '',
    first_day_of_week: 1
  });
  const currencyOptions = getCurrencyOptions(currencies, [businessData.base_currency_code]);
  const googleSignInEnabled = useGoogleSignInEnabled();

  useEffect(() => {
    fetchCountriesAndCurrencies();
  }, []);

  const fetchCountriesAndCurrencies = async () => {
    try {
      const [countriesRes, currenciesRes] = await Promise.all([
        authAPI.getCountries(),
        authAPI.getCurrencies()
      ]);

      if (countriesRes.data.success) {
        setCountries(countriesRes.data.data.countries);
      }

      if (currenciesRes.data.success) {
        setCurrencies(currenciesRes.data.data.currencies);
      }
    } catch (err) {
      console.error('Error fetching countries and currencies:', err);
    }
  };

  const handleUserSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (userData.password !== userData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordError = validateRegistrationPassword(userData.password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setStep(2);
  };

  const handleCountryChange = (countryCode: string) => {
    const country = countries.find(c => c.code === countryCode);
    if (country) {
      setBusinessData(prev => ({
        ...prev,
        country_code: countryCode,
        base_currency_code: country.default_currency_code,
        date_format: country.default_date_format,
        number_format: country.default_number_format,
        time_format: country.default_time_format,
        first_day_of_week: country.default_first_day_of_week
      }));
    }
  };

  const handleBusinessSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!businessData.name || !businessData.country_code) {
      setError('Business name and country are required');
      return;
    }

    setStep(3);
  };

  const handleCompleteRegistration = async () => {
    try {
      setLoading(true);
      setError(null);

      // Create user first
      const userResponse = await authAPI.register({
        email: userData.email,
        password: userData.password,
        first_name: userData.first_name,
        last_name: userData.last_name,
        role: 'admin' // First user becomes admin of their business
      });

      if (!userResponse.data.success) {
        throw new Error(userResponse.data.error || 'Failed to create user');
      }

      // Store token
      localStorage.setItem('token', userResponse.data.data.token);

      // Create business
      const businessResponse = await businessAPI.create(businessData);

      if (!businessResponse.data.success) {
        throw new Error(businessResponse.data.error || 'Failed to create business');
      }

      // User is automatically added as administrator to the business
      // This is handled in the backend business creation endpoint

      setStep(4); // Success step
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    onLogin();
  };

  if (step === 4) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <div className="success-icon">
              <span className="material-icons">check_circle</span>
            </div>
            <h1>Registration Complete!</h1>
            <p>Your account and business have been created successfully.</p>
          </div>
          
          <div className="success-details">
            <h3>Account Details</h3>
            <p><strong>Email:</strong> {userData.email}</p>
            <p><strong>Name:</strong> {userData.first_name} {userData.last_name}</p>
            <p><strong>Role:</strong> Administrator</p>
            
            <h3>Business Details</h3>
            <p><strong>Business Name:</strong> {businessData.name}</p>
            <p><strong>Country:</strong> {businessData.country_code}</p>
            <p><strong>Currency:</strong> {businessData.base_currency_code}</p>
          </div>

          <button 
            className="login-btn"
            onClick={() => window.location.reload()}
          >
            Continue to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>Create Account</h1>
          <p>Step {step} of 3: {step === 1 ? 'Personal Information' : step === 2 ? 'Business Information' : 'Complete Registration'}</p>
        </div>

        {step === 1 && googleSignInEnabled === true && (
          <>
            <GoogleAuthButton mode="signup" disabled={loading} />
            <div className="auth-divider">
              <span>or register with email</span>
            </div>
          </>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {step === 1 && (
          <form onSubmit={handleUserSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="first_name">First Name</label>
              <input
                type="text"
                id="first_name"
                value={userData.first_name}
                onChange={(e) => setUserData(prev => ({ ...prev, first_name: e.target.value }))}
                required
                placeholder="Enter your first name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="last_name">Last Name</label>
              <input
                type="text"
                id="last_name"
                value={userData.last_name}
                onChange={(e) => setUserData(prev => ({ ...prev, last_name: e.target.value }))}
                required
                placeholder="Enter your last name"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={userData.email}
                onChange={(e) => setUserData(prev => ({ ...prev, email: e.target.value }))}
                required
                placeholder="Enter your email"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={userData.password}
                onChange={(e) => setUserData(prev => ({ ...prev, password: e.target.value }))}
                required
                placeholder="Min 8 chars, upper, lower, number, special (@$!%*?&)"
                minLength={8}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={userData.confirmPassword}
                onChange={(e) => setUserData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                required
                placeholder="Confirm your password"
              />
            </div>
            
            <button type="submit" className="login-btn">
              Next: Business Information
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleBusinessSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="business_name">Business Name *</label>
              <input
                type="text"
                id="business_name"
                value={businessData.name}
                onChange={(e) => setBusinessData(prev => ({ ...prev, name: e.target.value }))}
                required
                placeholder="Enter your business name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="operational_name">Operational Name</label>
              <input
                type="text"
                id="operational_name"
                value={businessData.operational_name}
                onChange={(e) => setBusinessData(prev => ({ ...prev, operational_name: e.target.value }))}
                placeholder="Enter operational name (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="business_email">Business Email</label>
              <input
                type="email"
                id="business_email"
                value={businessData.email}
                onChange={(e) => setBusinessData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="business@example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="business_phone">Phone</label>
              <input
                type="tel"
                id="business_phone"
                value={businessData.phone}
                onChange={(e) => setBusinessData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+1-555-0123"
              />
            </div>

            <div className="form-group">
              <label htmlFor="address">Address</label>
              <textarea
                id="address"
                value={businessData.address}
                onChange={(e) => setBusinessData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Enter business address"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="tax_id">Tax ID</label>
              <input
                type="text"
                id="tax_id"
                value={businessData.tax_identification_number}
                onChange={(e) => setBusinessData(prev => ({ ...prev, tax_identification_number: e.target.value }))}
                placeholder="Tax identification number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="country">Country *</label>
              <select
                id="country"
                value={businessData.country_code}
                onChange={(e) => handleCountryChange(e.target.value)}
                required
              >
                <option value="">Select Country</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="currency">Currency</label>
              <select
                id="currency"
                value={businessData.base_currency_code}
                onChange={(e) => setBusinessData(prev => ({ ...prev, base_currency_code: e.target.value }))}
              >
                {currencyOptions.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setStep(1)}
              >
                Back
              </button>
              <button type="submit" className="login-btn">
                Next: Complete Registration
              </button>
            </div>
          </form>
        )}

        {step === 3 && (
          <div className="registration-summary">
            <h3>Review Your Information</h3>
            
            <div className="summary-section">
              <h4>Personal Information</h4>
              <p><strong>Name:</strong> {userData.first_name} {userData.last_name}</p>
              <p><strong>Email:</strong> {userData.email}</p>
            </div>

            <div className="summary-section">
              <h4>Business Information</h4>
              <p><strong>Business Name:</strong> {businessData.name}</p>
              <p><strong>Country:</strong> {businessData.country_code}</p>
              <p><strong>Currency:</strong> {businessData.base_currency_code}</p>
              {businessData.email && <p><strong>Business Email:</strong> {businessData.email}</p>}
              {businessData.phone && <p><strong>Phone:</strong> {businessData.phone}</p>}
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setStep(2)}
              >
                Back
              </button>
              <button 
                className="login-btn"
                onClick={handleCompleteRegistration}
                disabled={loading}
              >
                {loading ? 'Creating Account...' : 'Create Account & Business'}
              </button>
            </div>
          </div>
        )}
        
        <div className="login-footer">
          <p>
            Already have an account?{' '}
            <button 
              type="button" 
              className="link-button"
              onClick={handleLogin}
            >
              Sign in here
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
